import type {
  ActionResult,
  GameSession,
  ModuleChoice,
  ModuleManifest,
  Requirement,
  StateEffect,
  TranscriptEntry,
} from "../contracts/types";
import { GAME_SAVE_SCHEMA_VERSION } from "../contracts/types";
import { validateModule } from "../validation/validateModule";
import { createCampaignEvent } from "../loremine";

function clone<T>(value: T): T {
  return structuredClone(value);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

export function hashText(text: string): number {
  let hash = 2166136261;
  for (const char of text) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function evaluateCondition(
  req: Requirement,
  session: GameSession,
): boolean {
  if (req.type === "flag") return session.flags.includes(req.value);
  if (req.type === "inventory") return session.inventory.includes(req.value);
  if (req.type === "attribute")
    return (session.attributes[req.key] ?? 0) >= req.minimum;
  return false;
}

export function applyEffects(
  session: GameSession,
  effects: StateEffect[] = [],
): void {
  for (const effect of effects) {
    if (effect.type === "flag")
      session.flags = unique([...session.flags, effect.value]);
    if (effect.type === "discover")
      session.discovered = unique([...session.discovered, effect.sceneId]);
    if (effect.type === "lore")
      session.lore = unique([...session.lore, effect.value]);
    if (effect.type === "inventory")
      session.inventory = unique([...session.inventory, effect.value]);
    if (effect.type === "resource")
      session.resources[effect.key] = Math.max(
        0,
        (session.resources[effect.key] ?? 0) + effect.delta,
      );
  }
}

export function createSession(
  module: ModuleManifest,
  seed = "ARCHIVIST-47",
): GameSession {
  const errors = validateModule(module);
  if (errors.length) throw new Error(errors.join("\n"));
  const state = clone(module.startingState);
  const now = new Date().toISOString();
  return {
    authorityClass: "GAME_SESSION",
    moduleId: module.id,
    moduleVersion: module.version,
    saveSchemaVersion: GAME_SAVE_SCHEMA_VERSION,
    sessionId: `session-${hashText(`${module.id}:${seed}`).toString(16)}`,
    seed,
    currentSceneId: module.startSceneId,
    selectedSceneId: module.startSceneId,
    attributes: state.attributes,
    resources: state.resources,
    inventory: [...state.inventory],
    flags: [...state.flags],
    lore: [...state.lore],
    discovered: [...state.discovered],
    visited: [module.startSceneId],
    transcript: [
      {
        kind: "SYSTEM",
        text: `MODULE ${module.title} ONLINE // DEVELOPMENT FIXTURE / NON-AUTHORITATIVE`,
        at: now,
      },
    ],
    history: [],
    actionCount: 0,
    ending: null,
    saveRevision: 0,
    updatedAt: now,
    events: [], campaignReviews: [],
  };
}

export function resetSession(
  module: ModuleManifest,
  seed?: string,
): GameSession {
  return createSession(module, seed ?? "ARCHIVIST-47");
}

export function enterScene(
  module: ModuleManifest,
  sessionInput: GameSession,
  sceneId: string,
): GameSession {
  const session = clone(sessionInput);
  if (!session.discovered.includes(sceneId))
    throw new Error(`Scene not discovered: ${sceneId}`);
  const scene = module.scenes.find((s) => s.id === sceneId);
  if (!scene) throw new Error(`Unknown scene: ${sceneId}`);
  session.selectedSceneId = sceneId;
  // Focus without travel does not change current unless MOVE-like already did
  session.updatedAt = new Date().toISOString();
  return session;
}

export function visibleActions(
  module: ModuleManifest,
  session: GameSession,
): ModuleChoice[] {
  const scene = module.scenes.find((s) => s.id === session.currentSceneId);
  return (scene?.actions ?? []).filter((action) =>
    (action.requires ?? []).every((req) => evaluateCondition(req, session)),
  );
}

export function resolveCheck(
  session: GameSession,
  check: { attribute: string; target: number; salt: string },
  actionCount: number,
): { roll: number; attribute: number; total: number; success: boolean } {
  const roll =
    (hashText(`${session.seed}:${check.salt}:${actionCount}`) % 10) + 1;
  const attribute = session.attributes[check.attribute] ?? 0;
  const total = roll + attribute;
  return {
    roll,
    attribute,
    total,
    success: total >= check.target,
  };
}

/**
 * Map free-text or verb phrases to declared action IDs only.
 * Fail closed when no declared interaction matches.
 */
export function canonicalizeCommand(
  module: ModuleManifest,
  session: GameSession,
  input: string,
): string | null {
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  const actions = visibleActions(module, session);
  const exact = actions.find(
    (action) =>
      normalized === action.id ||
      normalized === action.label.toLowerCase(),
  );
  if (exact) return exact.id;
  const scored = actions
    .map((action) => {
      let score = 0;
      if (action.verb && normalized.includes(action.verb.toLowerCase()))
        score += 3;
      // JACK_IN synonym
      if (
        action.verb === "JACK" ||
        action.verb === "JACK_IN" ||
        action.id.includes("jack")
      ) {
        if (normalized.includes("jack")) score += 3;
      }
      for (const target of action.targets ?? [])
        if (normalized.includes(target.toLowerCase())) score += 2;
      for (const word of action.label.toLowerCase().split(/\s+/))
        if (word.length > 3 && normalized.includes(word)) score += 1;
      return { id: action.id, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored[0]?.score > 0 ? scored[0].id : null;
}

function pushTranscript(
  session: GameSession,
  entry: TranscriptEntry,
): void {
  session.transcript.push({ ...entry, at: entry.at ?? new Date().toISOString() });
}

/**
 * Resolve a declared choice/interaction. Idempotent when the same operationId
 * is resubmitted as the last successful operation.
 */
export function resolveChoice(
  module: ModuleManifest,
  sessionInput: GameSession,
  actionId: string,
  operationId?: string,
): ActionResult {
  const session = clone(sessionInput);
  if (session.ending)
    return { ok: false, error: "Session already ended", session };

  const opId =
    operationId ??
    `op-${hashText(`${session.sessionId}:${session.actionCount + 1}:${actionId}`).toString(16)}`;

  if (session.lastOperationId === opId) {
    return { ok: true, session: sessionInput, operationId: opId, duplicate: true };
  }

  const scene = module.scenes.find((s) => s.id === session.currentSceneId);
  if (!scene)
    return { ok: false, error: "Current scene missing", session };

  const action = visibleActions(module, session).find((a) => a.id === actionId);
  if (!action)
    return { ok: false, error: `Illegal action: ${actionId}`, session };

  session.actionCount += 1;
  pushTranscript(session, { kind: "PLAYER", text: `> ${action.label}` });

  let target = action.target ?? session.currentSceneId;
  let outcome = "APPLIED";

  if (action.check) {
    const result = resolveCheck(session, action.check, session.actionCount);
    outcome = result.success ? "SUCCESS" : "FAILURE";
    pushTranscript(session, {
      kind: "RULE",
      text: `${action.check.attribute.toUpperCase()} ${result.attribute} + SIGNAL ${result.roll} = ${result.total} / TARGET ${action.check.target} // ${outcome}`,
    });
    applyEffects(
      session,
      result.success ? action.successEffects : action.failureEffects,
    );
    target = (result.success ? action.successTarget : action.failureTarget) ??
      session.currentSceneId;
  } else {
    applyEffects(session, action.effects);
  }

  if (action.consequence)
    pushTranscript(session, { kind: "NARRATION", text: action.consequence });

  if (target && target !== session.currentSceneId) {
    session.currentSceneId = target;
    session.selectedSceneId = target;
    session.visited = unique([...session.visited, target]);
    session.discovered = unique([...session.discovered, target]);
    const next = module.scenes.find((s) => s.id === target);
    pushTranscript(session, {
      kind: "SYSTEM",
      text: `ROUTE TRAVERSED // ${next?.title ?? target}`,
    });
    if (next?.ending) session.ending = next.ending;
    if (next?.type === "ENDING" && next.ending) session.ending = next.ending;
  }

  session.history.push({
    index: session.actionCount,
    sceneId: scene.id,
    actionId,
    outcome,
    target,
    operationId: opId,
  });
  session.events.push(createCampaignEvent(session, session.history.length - 1));
  session.lastOperationId = opId;
  session.saveRevision += 1;
  session.updatedAt = new Date().toISOString();
  return { ok: true, session, operationId: opId, duplicate: false };
}

export function resolveInteraction(
  module: ModuleManifest,
  session: GameSession,
  input: string,
  operationId?: string,
): ActionResult {
  const actionId = canonicalizeCommand(module, session, input);
  if (!actionId)
    return {
      ok: false,
      error: `Undeclared command: ${input}`,
      session,
    };
  return resolveChoice(module, session, actionId, operationId);
}

/** Convenience alias matching proof package naming. */
export function submitAction(
  module: ModuleManifest,
  session: GameSession,
  actionId: string,
  operationId?: string,
): GameSession {
  const result = resolveChoice(module, session, actionId, operationId);
  if (!result.ok) throw new Error(result.error);
  return result.session;
}
